import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type {
  Bot,
  ComputerReleaseReason,
  ComputerStatus,
  Run,
  TaughtSkill,
} from "@rakazo/contracts";
import { BotAvatar, Button } from "@rakazo/ui-web";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { ComputerMaintenanceActions } from "../../components/ComputerMaintenanceActions";
import { ComputerScreenFrame } from "../../components/ComputerScreenFrame";
import { ComputerTools } from "../../components/ComputerTools";
import { TeachCaptureOverlay } from "../../components/teach/TeachCaptureOverlay";
import { TeachComputerOverlayControl } from "../../components/teach/TeachComputerOverlay";
import { TeachRecordingChrome, TeachStopButton } from "../../components/teach/TeachRecordingChrome";
import { useWorkspaceDesktop } from "../../components/WorkspaceSession";
import { computerTakeoverBlocked } from "../../lib/thread-events";
import { ComputerReleaseActions } from "./computer-release-actions";
import { computerLabel, DesktopKindEmptyState } from "./computer-view";

type ComputerOverlayProps = {
  bot: Bot;
  computer: ComputerStatus | null;
  computerViewport: { height: number; offsetTop: number } | null;
  recordingSkill: TaughtSkill | null;
  teachBusy: boolean;
  hasControl: boolean;
  composerRunning: boolean;
  sending: boolean;
  booting: boolean;
  runStatus?: Run["status"];
  sendError: string | null;
  embeddedScreenUrl: string | null;
  screenFrameRef: (frame: HTMLIFrameElement | null) => void;
  computerScreenError: ReactNode;
  computerBooting: ReactNode;
  onStopTeaching: () => void | Promise<void>;
  onStopRun: () => void;
  onRelease: (reason?: ComputerReleaseReason) => Promise<void>;
  onTakeControl: () => void;
  onRefreshTeaching: () => Promise<void>;
  onRefreshComputer: () => Promise<void>;
  onClose: () => void;
};

export function ComputerOverlay({
  bot,
  computer,
  computerViewport,
  recordingSkill,
  teachBusy,
  hasControl,
  composerRunning,
  sending,
  booting,
  runStatus,
  sendError,
  embeddedScreenUrl,
  screenFrameRef,
  computerScreenError,
  computerBooting,
  onStopTeaching,
  onStopRun,
  onRelease,
  onTakeControl,
  onRefreshTeaching,
  onRefreshComputer,
  onClose,
}: ComputerOverlayProps) {
  const workspaceDesktop = useWorkspaceDesktop();
  return (
    <div className={`${workspaceDesktop ? "absolute" : "fixed"} inset-0 z-50 bg-background`}>
      <div
        data-testid="computer-viewport"
        className={`${workspaceDesktop ? "absolute" : "fixed"} inset-x-0 top-0 flex flex-col bg-background pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]`}
        style={{
          height: workspaceDesktop
            ? "100%"
            : computerViewport
              ? `${computerViewport.height}px`
              : "100dvh",
          top:
            !workspaceDesktop && computerViewport ? `${computerViewport.offsetTop}px` : undefined,
        }}
      >
        <div
          data-testid="computer-chrome"
          className="app-no-drag flex flex-wrap items-center justify-between gap-2 border-b border-sidebar-border px-[18px] py-3.5"
        >
          <div className="flex min-w-0 flex-1 basis-56 items-center gap-3">
            <BotAvatar color={bot.color} identity={bot.id} size={28} status={bot.status} />
            {recordingSkill ? (
              <TeachRecordingChrome
                recording={recordingSkill}
                botId={bot.id}
                busy={teachBusy}
                onStop={onStopTeaching}
                variant="overlay"
              />
            ) : (
              <span
                className="min-w-0 truncate text-[15.5px] font-medium text-foreground"
                dir="auto"
              >
                {computerLabel(computer?.mode, bot.name, computer)}
              </span>
            )}
            {!recordingSkill && hasControl ? (
              computer?.takeoverRequested ? (
                <span className="shrink-0 whitespace-nowrap rounded-full bg-warning/15 px-[11px] py-1 text-[13px] text-warning">
                  <Trans>Needs you</Trans>
                </span>
              ) : (
                <span className="shrink-0 whitespace-nowrap rounded-full bg-success/15 px-[11px] py-1 text-[13px] text-success">
                  <Trans>You have control</Trans>
                </span>
              )
            ) : null}
          </div>
          <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
            {composerRunning ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label={t`Stop`}
                data-testid="computer-overlay-stop"
                onClick={() => void onStopRun()}
                disabled={sending}
              >
                <Trans>Stop</Trans>
              </Button>
            ) : null}
            {recordingSkill ? (
              <TeachStopButton busy={teachBusy} onStop={onStopTeaching} />
            ) : hasControl ? (
              <ComputerReleaseActions
                takeoverRequested={Boolean(computer?.takeoverRequested)}
                onRelease={onRelease}
              />
            ) : computer?.state === "running" && !computerTakeoverBlocked(computer, runStatus) ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={booting}
                onClick={() => void onTakeControl()}
              >
                <Trans>Take control</Trans>
              </Button>
            ) : null}
            {!recordingSkill ? (
              <TeachComputerOverlayControl
                key={bot.id}
                botId={bot.id}
                computer={computer}
                busy={teachBusy}
                onRefresh={onRefreshTeaching}
              />
            ) : null}
            {!recordingSkill ? (
              <ComputerMaintenanceActions
                botId={bot.id}
                computer={computer}
                onChanged={async () => {
                  await onRefreshComputer();
                }}
              />
            ) : null}
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground"
              aria-label={t`Close computer`}
              onClick={() => onClose()}
            >
              <X size={16} strokeWidth={1.8} />
            </Button>
          </div>
        </div>
        {sendError ? (
          <div
            role="alert"
            className="border-b border-destructive/40 bg-destructive/10 px-[18px] py-2 text-[13px] text-destructive"
          >
            {sendError}
          </div>
        ) : null}
        <div className="relative min-h-0 flex-1 bg-background">
          <ComputerTools
            key={bot.id}
            botId={bot.id}
            hasControl={hasControl}
            recording={Boolean(recordingSkill)}
          >
            {computer?.kind === "desktop" && !bot.deviceId && !embeddedScreenUrl ? (
              <DesktopKindEmptyState className="grid h-full place-items-center px-8 text-center text-sm text-muted-foreground/80" />
            ) : computer?.state === "running" && embeddedScreenUrl && !computerScreenError ? (
              <>
                <ComputerScreenFrame
                  key={embeddedScreenUrl}
                  title={t`Bot screen`}
                  frameRef={screenFrameRef}
                  url={embeddedScreenUrl}
                  allow="clipboard-read; clipboard-write; fullscreen"
                  style={{
                    pointerEvents: recordingSkill || !hasControl ? "none" : "auto",
                  }}
                />
                {bot ? (
                  <TeachCaptureOverlay
                    botId={bot.id}
                    skill={recordingSkill}
                    enabled={Boolean(recordingSkill)}
                    screenWidth={computer?.screenWidth}
                    screenHeight={computer?.screenHeight}
                  />
                ) : null}
              </>
            ) : (
              <div className="grid h-full place-items-center text-sm text-muted-foreground/80">
                {computerScreenError ??
                  computerBooting ??
                  (computer?.state === "suspended"
                    ? t`Computer is asleep`
                    : computerLabel(computer?.mode, bot.name, computer))}
              </div>
            )}
          </ComputerTools>
        </div>
      </div>
    </div>
  );
}
